import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { App } from 'antd';
import { setAuthorizationBase } from 'config/axiosBase';
import * as url from 'config/url_api';
import { LocalStorageKey } from 'utils/common/const';
import {
  authClientSignIn,
  getFP,
  getIP,
  getPlayers,
  getTeams,
  getWorldcupPredictionByEndpoint,
  submitWorldcupVoteByEndpoint
} from 'service/base';
import BoardTabs from './resultPredictionBoard/BoardTabs';
import RightPredictionPanel from './resultPredictionBoard/RightPredictionPanel';
import TopVoteList from './resultPredictionBoard/TopVoteList';
import VoteAuthModal from './resultPredictionBoard/VoteAuthModal';
import VoteConfirmModal from './resultPredictionBoard/VoteConfirmModal';
import {
  categoryTabs,
  fallbackRankingData,
  heroAvatar,
  tabEndpointMap
} from './resultPredictionBoard/constants';

/** Cùng thứ tự với `categoryTabs` */
const voteSubmitEndpointByTab = {
  'Đội vô địch': url.API_WORLD_CUP_CHAMPION_VOTE,
  'Vua phá lưới': url.API_WORLD_CUP_TOP_SCORER_VOTE,
  'Đội nhận thẻ đỏ đầu tiên': url.API_WORLD_CUP_TEAMS_FIRST_RED_CARD_VOTE,
  'Cầu thủ xuất sắc nhất': url.API_WORLD_CUP_BEST_PLAYER_VOTE,
  'Đội Châu Á Vào Sâu Nhất': url.API_WORLD_CUP_FINAL_ASIA_TEAM_VOTE
};

const ResultPredictionBoard = ({ activeTabIndex: controlledTabIndex, onTabIndexChange }) => {
  const [localTabIndex, setLocalTabIndex] = useState(0);
  const [topVoteData, setTopVoteData] = useState([]);
  const [rightBoardData, setRightBoardData] = useState([]);
  const [selectedRankIndex, setSelectedRankIndex] = useState(0);
  const [searchText, setSearchText] = useState('');
  const [searchError, setSearchError] = useState('');
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [isVotePopupOpen, setIsVotePopupOpen] = useState(false);
  const [isVoteConfirmOpen, setIsVoteConfirmOpen] = useState(false);
  const [accountName, setAccountName] = useState('');
  const [bankLastFourDigits, setBankLastFourDigits] = useState('');
  const [isSubmittingVote, setIsSubmittingVote] = useState(false);
  const [isSubmittingFinalVote, setIsSubmittingFinalVote] = useState(false);
  const [clientIp, setClientIp] = useState('');
  const [clientFp, setClientFp] = useState('');

  const tabRefs = useRef({});
  const tabScrollRef = useRef(null);
  const bankDigitRefs = useRef([]);
  const { notification } = App.useApp();

  const activeTabIndex =
    typeof controlledTabIndex === 'number' && controlledTabIndex >= 0
      ? controlledTabIndex
      : localTabIndex;

  const activeTab = categoryTabs[activeTabIndex] || categoryTabs[0];

  const setTabIndex = (nextIndex) => {
    if (typeof onTabIndexChange === 'function') {
      onTabIndexChange(nextIndex);
      return;
    }
    setLocalTabIndex(nextIndex);
  };

  const setTabByName = (tabName) => {
    const nextIndex = categoryTabs.findIndex((tab) => tab === tabName);
    if (nextIndex >= 0) {
      setTabIndex(nextIndex);
    }
  };

  const title = useMemo(() => activeTab.toUpperCase(), [activeTab]);
  const isTopScorerTab = activeTab === 'Vua phá lưới';
  const isBestPlayerTab = activeTab === 'Cầu thủ xuất sắc nhất';
  const isAsiaDeepTab = activeTab === 'Đội Châu Á Vào Sâu Nhất';
  const isPlayerTab = isTopScorerTab || isBestPlayerTab;
  const hideCountryOnCard = !isTopScorerTab && !isBestPlayerTab;

  const extractArrayFromResponse = (res) => {
    if (Array.isArray(res)) return res;
    if (Array.isArray(res?.data)) return res.data;
    if (Array.isArray(res?.data?.data)) return res.data.data;
    return [];
  };

  useEffect(() => {
    const bootstrapClientInfo = async () => {
      try {
        const [ipRes, fpRes] = await Promise.all([getIP(), getFP()]);
        setClientIp(typeof ipRes === 'string' ? ipRes : ipRes?.ip || ipRes?.data || '');
        setClientFp(typeof fpRes === 'string' ? fpRes : fpRes?.fp || fpRes?.data || '');
      } catch (error) {}
    };

    bootstrapClientInfo();
  }, []);

  useEffect(() => {
    let isMounted = true;

    const fetchTopVoteByTab = async () => {
      const endpoint = tabEndpointMap[activeTab];
      if (!endpoint) {
        setTopVoteData([]);
        return;
      }

      try {
        const res = await getWorldcupPredictionByEndpoint(endpoint);
        if (!isMounted) return;
        setTopVoteData(extractArrayFromResponse(res));
      } catch (e) {
        if (!isMounted) return;
        setTopVoteData([]);
      }
    };

    fetchTopVoteByTab();

    return () => {
      isMounted = false;
    };
  }, [activeTab]);

  useEffect(() => {
    let isMounted = true;

    const fetchRightBoardData = async () => {
      try {
        let res;
        if (isPlayerTab) {
          res = await getPlayers();
        } else if (isAsiaDeepTab) {
          res = await getWorldcupPredictionByEndpoint(url.API_WORLD_CUP_FINAL_ASIA_TEAM);
        } else {
          res = await getTeams();
        }
        if (!isMounted) return;

        const rawData = extractArrayFromResponse(res);
        setRightBoardData(rawData);
      } catch (e) {
        if (!isMounted) return;
        setRightBoardData([]);
      }
    };

    fetchRightBoardData();

    return () => {
      isMounted = false;
    };
  }, [isPlayerTab, isAsiaDeepTab]);

  const resolveDisplayCount = useCallback((item, fallbackValue) => {
    if (item?.count !== undefined && item?.count !== null) return item.count;
    if (item?.point !== undefined && item?.point !== null) return item.point;
    if (item?.points !== undefined && item?.points !== null) return item.points;
    if (item?.score !== undefined && item?.score !== null) return item.score;
    return fallbackValue;
  }, []);

  const mapTopVoteItem = useCallback((item, idx, total) => ({
    name:
      item?.name_vi ||
      item?.name ||
      item?.playerName ||
      item?.player_name ||
      item?.teamName ||
      item?.team_name ||
      'Unknown',
    avatar: item?.avatar || item?.flag || item?.image || item?.logo || heroAvatar,
    teamName: item?.teamName || item?.team_name || item?.country || item?.countryName || item?.code || '',
    point: resolveDisplayCount(item, total - idx)
  }), [resolveDisplayCount]);

  const mapRightBoardItem = useCallback((item, idx, total) => ({
    name: item?.name_vi || item?.name || 'Unknown',
    avatar: item?.avatar || item?.flag || item?.image || item?.logo || heroAvatar,
    teamName: item?.teamName || item?.team_name || item?.country || item?.countryName || item?.code || '',
    point: resolveDisplayCount(item, total - idx)
  }), [resolveDisplayCount]);

  const leftRankingList = useMemo(() => {
    if (topVoteData.length > 0) {
      return topVoteData.map((item, idx) => mapTopVoteItem(item, idx, topVoteData.length));
    }
    return fallbackRankingData;
  }, [mapTopVoteItem, topVoteData]);

  const rightRankingList = useMemo(() => {
    if (rightBoardData.length > 0) {
      return rightBoardData.map((item, idx) => mapRightBoardItem(item, idx, rightBoardData.length));
    }
    return fallbackRankingData;
  }, [mapRightBoardItem, rightBoardData]);

  const normalizeText = (value = '') =>
    value
      .toString()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .trim();

  const findBestMatchIndex = (rawKeyword) => {
    const keyword = normalizeText(rawKeyword);
    if (!keyword) return -1;

    const keywords = keyword.split(/\s+/).filter(Boolean);
    let bestIndex = -1;
    let bestScore = -1;

    rightRankingList.forEach((item, idx) => {
      const candidate = normalizeText(item?.name || '');
      if (!candidate) return;

      let score = 0;
      if (candidate === keyword) score += 1000;
      if (candidate.startsWith(keyword)) score += 300;
      if (candidate.includes(keyword)) score += 200;

      keywords.forEach((token) => {
        if (candidate === token) score += 600;
        if (candidate.startsWith(token)) score += 140;
        if (candidate.includes(token)) score += 80;
      });

      score -= Math.abs(candidate.length - keyword.length) * 0.5;

      if (score > bestScore) {
        bestScore = score;
        bestIndex = idx;
      }
    });

    return bestScore > 0 ? bestIndex : -1;
  };

  const handleSearchCandidate = (rawKeyword = searchText) => {
    const keywordToSearch = rawKeyword || podiumData.center?.name || '';
    const foundIndex = findBestMatchIndex(keywordToSearch);

    if (foundIndex >= 0) {
      setSelectedRankIndex(foundIndex);
      setSearchError('');
      return;
    }

    setSearchError('Không tìm thấy tên phù hợp');
  };

  const suggestionList = useMemo(() => {
    const keyword = normalizeText(searchText);
    if (!keyword) {
      return rightRankingList.map((item, idx) => ({ ...item, index: idx }));
    }

    return rightRankingList
      .map((item, idx) => ({ ...item, index: idx }))
      .filter((item) => normalizeText(item?.name || '').includes(keyword));
  }, [rightRankingList, searchText]);

  const canVote = useMemo(() => {
    const keyword = normalizeText(searchText);
    if (!keyword) return false;
    return rightRankingList.some((item) => normalizeText(item?.name || '') === keyword);
  }, [rightRankingList, searchText]);

  const handleSelectSuggestion = (item) => {
    setSearchText(item?.name || '');
    setSelectedRankIndex(item?.index ?? 0);
    setSearchError('');
    setIsSearchFocused(false);
  };

  const handleOpenVotePopup = () => {
    if (!canVote) {
      notification.warning({
        message: 'Cảnh báo',
        description: 'Vui lòng chọn tên hợp lệ trước khi bình chọn.'
      });
      return;
    }

    const savedUsername = localStorage.getItem('client_signin_username');
    const userToken = localStorage.getItem(LocalStorageKey.ACCESS_TOKEN);

    if (savedUsername && userToken) {
      setIsVoteConfirmOpen(true);
      return;
    }

    setAccountName('');
    setBankLastFourDigits('');
    setIsVotePopupOpen(true);
  };

  const handleCloseVotePopup = () => {
    setIsVotePopupOpen(false);
    setAccountName('');
    setBankLastFourDigits('');
  };

  const handleCloseVoteConfirm = () => {
    setIsVoteConfirmOpen(false);
  };

  const handleConfirmVote = async () => {
    const username = accountName.trim();
    const lastbank = bankLastFourDigits.trim();

    if (!username) {
      notification.warning({ message: 'Cảnh báo', description: 'Vui lòng nhập tên tài khoản.' });
      return;
    }

    if (!/^\d{4}$/.test(lastbank)) {
      notification.warning({
        message: 'Cảnh báo',
        description: 'Vui lòng nhập đúng 4 số cuối tài khoản ngân hàng.'
      });
      return;
    }

    if (!canVote) {
      notification.warning({
        message: 'Cảnh báo',
        description: 'Vui lòng chọn tên hợp lệ trước khi bình chọn.'
      });
      return;
    }

    try {
      setIsSubmittingVote(true);
      const res = await authClientSignIn({ username, lastbank, site: 'qq88' });

      if (res?.code === 0 || res?.success === true) {
        const userToken =
          res?.userToken || res?.token || res?.accessToken || res?.data?.userToken || res?.data?.token;

        localStorage.setItem('client_signin_username', username);
        if (userToken) {
          localStorage.setItem(LocalStorageKey.ACCESS_TOKEN, String(userToken));
          setAuthorizationBase(String(userToken));
        }

        window.dispatchEvent(new Event('client-signin-updated'));
        handleCloseVotePopup();
        notification.success({
          message: 'Thành công',
          description: 'Kiểm tra tài khoản thành công.'
        });
        return;
      }

      notification.error({
        message: 'Lỗi',
        description: res?.message || res?.text_mess || 'Gửi thông tin thất bại.'
      });
    } catch (error) {
      notification.error({
        message: 'Lỗi',
        description: error?.message || 'Không thể kết nối đến máy chủ.'
      });
    } finally {
      setIsSubmittingVote(false);
    }
  };

  const handleBankDigitChange = (idx, value) => {
    const digit = value.replace(/\D/g, '').slice(-1);
    const nextDigits = bankLastFourDigits.split('');
    while (nextDigits.length < 4) nextDigits.push('');
    nextDigits[idx] = digit;
    setBankLastFourDigits(nextDigits.join('').slice(0, 4));

    if (digit && idx < 3) {
      bankDigitRefs.current[idx + 1]?.focus();
    }
  };

  const handleBankDigitKeyDown = (idx, e) => {
    if (e.key === 'Backspace' && !bankLastFourDigits[idx] && idx > 0) {
      bankDigitRefs.current[idx - 1]?.focus();
    }
  };

  useEffect(() => {
    setSelectedRankIndex(0);
  }, [activeTab, rightRankingList.length]);

  useEffect(() => {
    setSearchText('');
    setSearchError('');
  }, [activeTab]);

  useEffect(() => {
    const scrollEl = tabScrollRef.current;
    const activeTabEl = tabRefs.current[activeTab];
    if (!activeTabEl || !scrollEl) return;

    const nextLeft = activeTabEl.offsetLeft - (scrollEl.clientWidth - activeTabEl.clientWidth) / 2;
    scrollEl.scrollTo({ left: Math.max(0, nextLeft), behavior: 'smooth' });
  }, [activeTab]);

  const podiumData = useMemo(() => {
    const source = rightRankingList.length > 0 ? rightRankingList : fallbackRankingData;
    const centerIndex = Math.min(Math.max(selectedRankIndex, 0), source.length - 1);
    const center = source[centerIndex] || source[0];
    const leftIndex = source.length > 1 ? (centerIndex - 1 + source.length) % source.length : centerIndex;
    const rightIndex = source.length > 1 ? (centerIndex + 1) % source.length : centerIndex;

    const left = { ...(source[leftIndex] || center), index: leftIndex };
    const centerWithIndex = { ...center, index: centerIndex };
    const right = { ...(source[rightIndex] || center), index: rightIndex };

    return { left, center: centerWithIndex, right };
  }, [rightRankingList, selectedRankIndex]);

  useEffect(() => {
    if (isSearchFocused) return;
    const centerName = podiumData.center?.name || '';
    if (!centerName) return;
    setSearchText(centerName);
    if (searchError) setSearchError('');
  }, [podiumData, isSearchFocused, searchError]);

  const handlePrevPodium = () => {
    const source = rightRankingList.length > 0 ? rightRankingList : fallbackRankingData;
    if (source.length <= 1) return;
    setSelectedRankIndex((prev) => (prev - 1 + source.length) % source.length);
  };

  const handleNextPodium = () => {
    const source = rightRankingList.length > 0 ? rightRankingList : fallbackRankingData;
    if (source.length <= 1) return;
    setSelectedRankIndex((prev) => (prev + 1) % source.length);
  };

  const handlePrevTab = () => {
    const prevIndex = (activeTabIndex - 1 + categoryTabs.length) % categoryTabs.length;
    setTabIndex(prevIndex);
  };

  const handleNextTab = () => {
    const nextIndex = (activeTabIndex + 1) % categoryTabs.length;
    setTabIndex(nextIndex);
  };

  const handleSearchChange = (e) => {
    const nextValue = e.target.value;
    setSearchText(nextValue);
    setIsSearchFocused(true);

    if (searchError) setSearchError('');
    if (!nextValue.trim()) return;

    const liveFoundIndex = findBestMatchIndex(nextValue);
    if (liveFoundIndex >= 0) {
      setSelectedRankIndex(liveFoundIndex);
    }
  };

  const handleSelectTopVoteRank = (item, fallbackIndex = 0) => {
    const selectedName = item?.name || '';
    const matchedIndex = rightRankingList.findIndex(
      (candidate) => normalizeText(candidate?.name || '') === normalizeText(selectedName)
    );

    const nextIndex = matchedIndex >= 0 ? matchedIndex : fallbackIndex;
    setSelectedRankIndex(nextIndex);
    if (selectedName && !isSearchFocused) {
      setSearchText(selectedName);
    }
    if (searchError) setSearchError('');
  };

  const handleSearchEnter = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearchCandidate();
    }
  };

  const handleConfirmVoteAction = async () => {
    const selectedName = podiumData.center?.name || searchText;
    if (!selectedName) {
      notification.warning({ message: 'Cảnh báo', description: 'Vui lòng chọn dữ liệu hợp lệ.' });
      return;
    }

    const token = localStorage.getItem(LocalStorageKey.ACCESS_TOKEN);
    if (!token) {
      setIsVoteConfirmOpen(false);
      setIsVotePopupOpen(true);
      notification.warning({
        message: 'Cảnh báo',
        description: 'Vui lòng kiểm tra tài khoản trước khi bình chọn.'
      });
      return;
    }

    const endpoint = voteSubmitEndpointByTab[activeTab];
    if (!endpoint) {
      notification.warning({
        message: 'Thông báo',
        description: 'Hạng mục này chưa hỗ trợ gửi bình chọn API.'
      });
      return;
    }

    const payloadBase = {
      ip: clientIp || '',
      fp: clientFp || '',
      site: 'qq88'
    };

    const payload = isPlayerTab
      ? { ...payloadBase, playerName: selectedName }
      : { ...payloadBase, teamName: selectedName };

    try {
      setIsSubmittingFinalVote(true);
      const res = await submitWorldcupVoteByEndpoint(endpoint, payload);

      if (res?.code === 0 || res?.success === true) {
        handleSearchCandidate(selectedName);
        setIsVoteConfirmOpen(false);
        notification.success({ message: 'Thành công', description: 'Bình chọn thành công.' });
        return;
      }

      notification.warning({
        message: 'Thông báo',
        description: res?.message || res?.text_mess || 'Gửi bình chọn thất bại.'
      });
    } catch (error) {
      notification.error({
        message: 'Lỗi',
        description: error?.message || 'Không thể kết nối đến máy chủ.'
      });
    } finally {
      setIsSubmittingFinalVote(false);
    }
  };

  return (
    <section className="relative z-10 mx-auto w-full max-w-[1236px]">
      <div className="rounded-[24px] border-[3px] border-[#006719] ring-[3px] ring-[#006719] bg-[#d7e6da] p-3 md:p-5">
        <BoardTabs
          categoryTabs={categoryTabs}
          activeTab={activeTab}
          tabRefs={tabRefs}
          tabScrollRef={tabScrollRef}
          onSelectTab={setTabByName}
          onPrevTab={handlePrevTab}
          onNextTab={handleNextTab}
        />

        <div className="grid gap-3 md:grid-cols-[340px_1fr] md:gap-4">
          <TopVoteList
            leftRankingList={leftRankingList}
            selectedRankIndex={selectedRankIndex}
            hideCountryOnCard={hideCountryOnCard}
            onSelectRank={handleSelectTopVoteRank}
          />

          <RightPredictionPanel
            title={title}
            podiumData={podiumData}
            hideCountryOnCard={hideCountryOnCard}
            searchText={searchText}
            isSearchFocused={isSearchFocused}
            suggestionList={suggestionList}
            canVote={canVote}
            searchError={searchError}
            onPrevPodium={handlePrevPodium}
            onNextPodium={handleNextPodium}
            onSelectRank={setSelectedRankIndex}
            onSearchFocus={() => setIsSearchFocused(true)}
            onSearchBlur={() => setTimeout(() => setIsSearchFocused(false), 100)}
            onSearchChange={handleSearchChange}
            onSearchEnter={handleSearchEnter}
            onSelectSuggestion={handleSelectSuggestion}
            onOpenVotePopup={handleOpenVotePopup}
          />
        </div>
      </div>

      <VoteAuthModal
        open={isVotePopupOpen}
        accountName={accountName}
        bankLastFourDigits={bankLastFourDigits}
        bankDigitRefs={bankDigitRefs}
        isSubmittingVote={isSubmittingVote}
        onAccountNameChange={setAccountName}
        onBankDigitChange={handleBankDigitChange}
        onBankDigitKeyDown={handleBankDigitKeyDown}
        onClose={handleCloseVotePopup}
        onConfirm={handleConfirmVote}
      />

      <VoteConfirmModal
        open={isVoteConfirmOpen}
        title={activeTab}
        selectedName={podiumData.center?.name || searchText || 'Unknown'}
        isSubmitting={isSubmittingFinalVote}
        onClose={handleCloseVoteConfirm}
        onConfirm={handleConfirmVoteAction}
      />
    </section>
  );
};

export default ResultPredictionBoard;

